import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    ABT_OVERRIDE_CELL_BG,
    DEFAULT_VALUE_TEXT,
    EDITABLE_CELL_BG,
    EXTRAPOLATED_CELL_BG,
    MANUAL_ACCENT_SHADOW,
    MISSING_CELL_BG,
    ORPHANED_ROW_BG,
    PENDING_WASH,
} from './pendingCellStyle';

/** The cell cues a Budget grid page can paint. Each page lists the ones it actually shows. */
export type LegendCue =
    | 'editable'
    | 'unsaved'
    | 'manualOverride'
    | 'extrapolated'
    | 'defaultOverride'
    | 'orphaned'
    | 'gap'
    | 'defaultValue';

// What the colours in a Budget grid mean (CALL-779). Four cues can share one cell — editable yellow,
// extrapolated blue, the manual-override bar, the unsaved wash — and none of them is guessable, so the
// sticky Save bar names them.
//
// The swatches are painted from the SAME constants as the cells (see pendingCellStyle), not from copies:
// a legend that quietly disagrees with the grid is worse than no legend. Each takes a faint border, or
// the pale backgrounds would be invisible against the bar's own white.
const SWATCH = {
    width: 14,
    height: 14,
    borderRadius: '2px',
    border: '1px solid',
    borderColor: 'divider',
    flexShrink: 0,
};

const CellLegend = ({ cues }: { cues: LegendCue[] }) => {
    const { t } = useTranslation('budget');

    // Written out per cue rather than interpolated from the cue name: each key has to appear as a literal
    // inside its own t() call for the EN/DE parity test to see it (it collects static keys only, and
    // `fallbackLng` is false, so a key missing from one language renders raw on screen).
    const items: Record<LegendCue, { label: string; sx: object; glyph?: string }> = {
        editable: { label: t('legend.editable'), sx: { backgroundColor: EDITABLE_CELL_BG } },
        unsaved: {
            label: t('legend.unsaved'),
            sx: { backgroundColor: EDITABLE_CELL_BG, backgroundImage: PENDING_WASH },
        },
        manualOverride: {
            label: t('legend.manualOverride'),
            sx: { backgroundColor: EDITABLE_CELL_BG, boxShadow: MANUAL_ACCENT_SHADOW },
        },
        extrapolated: { label: t('legend.extrapolated'), sx: { backgroundColor: EXTRAPOLATED_CELL_BG } },
        // CALL-822/824: one cue for both grids of the Preise & ABT page — Vertrags-ABT vs Standard-ABT,
        // Minutenpreis vs Standard-Minutenpreis.
        defaultOverride: { label: t('legend.defaultOverride'), sx: { backgroundColor: ABT_OVERRIDE_CELL_BG } },
        orphaned: { label: t('legend.orphaned'), sx: { backgroundColor: ORPHANED_ROW_BG } },
        // CALL-818/824: the detail dialog's two extra cues — the warned gap, and a default shown greyed
        // where nothing is entered (a text cue, so the swatch carries a glyph instead of a fill).
        gap: { label: t('legend.gap'), sx: { backgroundColor: MISSING_CELL_BG } },
        defaultValue: {
            label: t('legend.defaultValue'),
            glyph: '0',
            sx: {
                backgroundColor: EDITABLE_CELL_BG,
                color: DEFAULT_VALUE_TEXT,
                fontStyle: 'italic',
                fontSize: 10,
                lineHeight: '12px',
                textAlign: 'center',
            },
        },
    };

    return (
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
            {cues.map((cue) => (
                <Stack key={cue} direction="row" alignItems="center" spacing={0.5}>
                    <Box sx={{ ...SWATCH, ...items[cue].sx }}>{items[cue].glyph}</Box>
                    <Typography variant="caption" color="text.secondary">
                        <span>{items[cue].label}</span>
                    </Typography>
                </Stack>
            ))}
        </Stack>
    );
};

export default CellLegend;
